/**
 * Carrier portal sign-in page.
 *
 * Single-field form: email. On submit, POSTs to /api/carrier-auth/request-link,
 * which always returns 200 with a generic 'check your email' message
 * (deliberate — see backend comment about account enumeration).
 *
 * Two states:
 *   - 'idle': showing the form
 *   - 'sent':  showing the 'check your email' confirmation, with a 'send again' link
 *
 * If the carrier already has a valid session, we redirect them to /carrier/dashboard
 * immediately on mount.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Mail, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useCarrierSession, portalOf } from '@/auth/useCarrierSession';
import { carrierAuthRequestLink } from '@/api/client';

/**
 * ⭐ F212: the client portal's sign-in is this page at /client/login — the same magic link
 * (Pete: "Client should be the same as carrier for magic links"); only the words differ. The
 * link itself is sent to whichever portal the email belongs to.
 */
export function CarrierLoginPage({ portal = 'carrier' }: { portal?: 'carrier' | 'client' } = {}) {
    const { session } = useCarrierSession();
    const navigate = useNavigate();

    const [email, setEmail] = useState('');
    const [state, setState] = useState<'idle' | 'sending' | 'sent'>('idle');
    const [error, setError] = useState<string | null>(null);

    // If already signed in, send them straight to the dashboard.
    useEffect(() => {
        if (session) {
            navigate(`/${portalOf(session)}/dashboard`, { replace: true });
        }
    }, [session, navigate]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (state === 'sending') return;
        if (!email.trim()) {
            setError('Please enter your email address.');
            return;
        }

        setState('sending');
        setError(null);
        try {
            await carrierAuthRequestLink(email.trim());
            setState('sent');
        } catch (err: any) {
            setError(err.message ?? 'Something went wrong. Please try again.');
            setState('idle');
        }
    };

    const sendAnother = () => {
        setState('idle');
        setError(null);
    };

    return (
        <div className="min-h-[calc(100vh-64px)] grid place-items-center px-4 py-12">
            <div className="w-full max-w-md">
                {state === 'sent' ? (
                    <SentConfirmation email={email} onAnother={sendAnother} />
                ) : (
                    <LoginForm
                        email={email}
                        setEmail={setEmail}
                        onSubmit={submit}
                        sending={state === 'sending'}
                        error={error}
                        portal={portal}
                    />
                )}
            </div>
        </div>
    );
}

function LoginForm({
    email,
    setEmail,
    onSubmit,
    sending,
    error,
    portal,
}: {
    email: string;
    setEmail: (s: string) => void;
    onSubmit: (e: React.FormEvent) => void;
    sending: boolean;
    error: string | null;
    portal: 'carrier' | 'client';
}) {
    return (
        <>
            <h1 className="page-title mb-2">{portal === 'client' ? 'Client sign in' : 'Carrier sign in'}</h1>
            <p className="text-sm text-muted-foreground mb-8">
                Enter the email address you use with Symbia. We&rsquo;ll send you a one-click
                sign-in link &mdash; no password needed.
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
                <div>
                    <Label htmlFor="login-email" className="text-xs">Email</Label>
                    <Input
                        id="login-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={portal === 'client' ? 'you@your-company.com' : 'you@your-carrier.com'}
                        autoComplete="email"
                        autoFocus
                        disabled={sending}
                        required
                    />
                </div>

                {error && (
                    <div className="text-sm rounded-md p-3 bg-pipeline-rose-bg text-pipeline-rose-text border border-pipeline-rose-border flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                    </div>
                )}

                <Button type="submit" disabled={sending} className="w-full" size="lg">
                    {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                        <Mail className="h-4 w-4" />
                    )}
                    Send sign-in link
                </Button>
            </form>

            <p className="text-xs text-muted-foreground mt-8 text-center">
                Don&rsquo;t have an account yet? Just enter your email &mdash; we&rsquo;ll
                send your request to a Symbia scheduler for approval.
            </p>
        </>
    );
}

function SentConfirmation({ email, onAnother }: { email: string; onAnother: () => void }) {
    return (
        <div className="text-center">
            <div className="mx-auto mb-6 flex h-12 w-12 items-center justify-center rounded-full bg-pipeline-emerald-bg text-pipeline-emerald-text">
                <Mail className="h-6 w-6" />
            </div>
            <h1 className="page-title mb-2">Check your email</h1>
            <p className="text-sm text-muted-foreground mb-2">
                If <b className="text-foreground">{email}</b> is registered with Symbia,
                you&rsquo;ll receive a sign-in link in the next minute or two.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
                The link is valid for 15 minutes and can only be used once.
            </p>

            <div className="text-sm text-muted-foreground">
                Didn&rsquo;t get it?{' '}
                <button
                    type="button"
                    onClick={onAnother}
                    className="text-foreground underline underline-offset-2 hover:no-underline"
                >
                    Try again
                </button>
            </div>
        </div>
    );
}
